import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, BarChart3, Bell, TrendingUp, Monitor, Target, Shield, ChevronRight, Loader2, ArrowLeft, BookOpen, Plus, X, Video } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { db } from "../config/firebase";
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";

// Category data
const categories = [
    { id: "indicators", name: "Indicators", description: "Technical indicators and chart analysis", icon: BarChart3, color: "from-blue-500 to-blue-600" },
    { id: "alerts", name: "Alerts", description: "Price alerts and notifications setup", icon: Bell, color: "from-orange-500 to-orange-600" },
    { id: "tradingview", name: "TradingView", description: "TradingView platform tutorials", icon: TrendingUp, color: "from-emerald-500 to-emerald-600" },
    { id: "mt4mt5", name: "MT4/MT5", description: "MetaTrader platform guides", icon: Monitor, color: "from-purple-500 to-purple-600" },
    { id: "strategy", name: "Strategy", description: "Trading strategies and methods", icon: Target, color: "from-red-500 to-red-600" },
    { id: "risk", name: "Risk Management", description: "Position sizing and risk control", icon: Shield, color: "from-cyan-500 to-cyan-600" }
];

export default function KnowledgeBase() {
    const navigate = useNavigate();
    const { user } = useAuth() || { user: null };
    const [searchQuery, setSearchQuery] = useState("");
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);

    // Form state
    const [newArticle, setNewArticle] = useState({
        title: "",
        category: "strategy",
        content: "",
        videoUrl: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isAdmin = user?.role === 'admin';

    useEffect(() => {
        const articlesRef = collection(db, "knowledge_base");
        const q = query(articlesRef, orderBy("created_at", "desc"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newArticles = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setArticles(newArticles);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching articles:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleAddArticle = async (e) => {
        e.preventDefault();
        if (!newArticle.title || !newArticle.content) return;

        setIsSubmitting(true);
        try {
            await addDoc(collection(db, "knowledge_base"), {
                ...newArticle,
                created_at: serverTimestamp(),
                author_email: user.email,
                author_name: user?.firstName || "Admin"
            });
            setShowAddModal(false);
            setNewArticle({ title: "", category: "strategy", content: "", videoUrl: "" });
            alert("Article added successfully!");
        } catch (error) {
            console.error("Error adding article:", error);
            alert("Failed to add article.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getArticleCount = (categoryId) => articles.filter(a => a.category === categoryId).length;

    // Fallback dummy data if Firestore is empty or locked
    const dummyArticles = [
        { id: '1', category: 'alerts', title: 'How do I set up RSI alerts on TradingView?', content: "For RSI alerts: 1. Add RSI indicator to your chart 2. Click on the RSI line where you want the alert 3. Select 'Add Alert' 4. Set condition (e.g., 'Crossing Up' at 30 for oversold) 5. Choose notification method Pro tip: Use 'Once Per Bar Close' to avoid false signals during volatile moments!", contributor_name: 'Sarah Chen' },
        { id: '2', category: 'risk_management', title: 'What is the recommended risk per trade for swing trading?', content: 'The general rule is 1-2% of your total account per trade. Here\'s why: • 1% risk = can handle 10+ consecutive losses • 2% risk = more aggressive but still sustainable • Never exceed 5% total risk across all open positions. For swing trading specifically, I recommend starting at 1% until you have a proven track record.', contributor_name: 'David Park' },
        { id: '3', category: 'mt4_mt5', title: 'How do I install custom indicators on MT4?', content: 'To install custom indicators on MT4: 1. Download the .mq4 or .ex4 indicator file 2. Open MT4 and go to File > Open Data Folder 3. Navigate to MQL4 > Indicators folder 4. Copy your indicator file into this folder 5. Restart MT4 or right-click Navigator panel > Refresh 6. Find your indicator in Navigator > Indicators 7. Drag it onto your chart.', contributor_name: 'Sarah Chen' }
    ];

    const displayArticles = articles.length > 0 ? articles : dummyArticles;

    const filteredArticles = displayArticles.filter(article => {
        const matchesSearch = !searchQuery || article.title?.toLowerCase().includes(searchQuery.toLowerCase()) || article.content?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = !selectedCategory || article.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    // Helper to extract YouTube ID
    const getYoutubeId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">

            {/* Simple Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex justify-between items-start">
                        <div>
                            {/* Back Button */}
                            <button
                                onClick={() => navigate('/community')}
                                className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium text-sm mb-3 transition-colors"
                            >
                                <ArrowLeft size={18} />
                                <span>Back to Community</span>
                            </button>

                            {/* Breadcrumbs */}
                            <div className="flex items-center text-xs text-gray-500 mb-4 font-medium">
                                <Link to="/student/dashboard" className="hover:text-blue-600">Dashboard</Link>
                                <ChevronRight size={14} className="mx-2 text-gray-400" />
                                <Link to="/community" className="hover:text-blue-600">Community</Link>
                                <ChevronRight size={14} className="mx-2 text-gray-400" />
                                <span className="text-gray-900 bg-gray-100 px-2 py-1 rounded">Knowledge Base</span>
                            </div>

                            {/* Title */}
                            <div className="flex items-center">
                                <div className="bg-purple-500 p-2.5 rounded-lg mr-3">
                                    <BookOpen size={20} className="text-white" />
                                </div>
                                <div>
                                    <h1 className="text-lg md:text-xl font-bold text-gray-900">Knowledge Base</h1>
                                    <p className="text-xs text-gray-500">Trading guides & resources</p>
                                </div>
                            </div>
                        </div>

                        {/* Admin Add Button */}
                        {isAdmin && (
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Plus size={18} />
                                <span className="hidden md:inline">Add Content</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-6">
                {selectedArticle ? (
                    <div className="max-w-4xl mx-auto">
                        <button onClick={() => setSelectedArticle(null)} className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6 font-medium">
                            <ArrowLeft size={20} /> Back to Articles
                        </button>
                        <article className="bg-white rounded-2xl border border-gray-200 p-6 md:p-10 shadow-sm">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full uppercase">
                                    {categories.find(c => c.id === selectedArticle.category)?.name || "Article"}
                                </span>
                                <span className="text-gray-400 text-xs">
                                    By {selectedArticle.author_name}
                                </span>
                            </div>

                            <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">{selectedArticle.title}</h1>

                            {/* Video Player if available */}
                            {selectedArticle.videoUrl && getYoutubeId(selectedArticle.videoUrl) && (
                                <div className="mb-8 aspect-video rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm">
                                    <iframe
                                        width="100%"
                                        height="100%"
                                        src={`https://www.youtube.com/embed/${getYoutubeId(selectedArticle.videoUrl)}`}
                                        title={selectedArticle.title}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            )}

                            <div className="prose max-w-none text-gray-600 whitespace-pre-wrap leading-relaxed">
                                {selectedArticle.content}
                            </div>
                        </article>
                    </div>
                ) : (
                    <>
                        {/* Search Bar */}
                        <div className="relative max-w-xl mx-auto mb-10">
                            <input
                                type="text"
                                placeholder="Search tutorials, definitions, guides..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none shadow-sm text-gray-700 placeholder-gray-400 transition-all text-sm md:text-base"
                            />
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        </div>

                        {/* Category Filter */}
                        <div className="flex flex-wrap gap-2 justify-center mb-10">
                            <button
                                onClick={() => setSelectedCategory(null)}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!selectedCategory ? 'bg-purple-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                    }`}
                            >
                                All
                            </button>
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === cat.id ? 'bg-purple-600 text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                                        }`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>

                        {/* Content Grid */}
                        {loading ? (
                            <div className="flex justify-center py-20">
                                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                            </div>
                        ) : filteredArticles.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredArticles.map((article) => {
                                    const category = categories.find(c => c.id === article.category);
                                    // Default fallback colors if category not found
                                    const gradientColor = category?.color || "from-gray-500 to-gray-600";
                                    const Icon = category?.icon || BookOpen;

                                    return (
                                        <div
                                            key={article.id}
                                            onClick={() => setSelectedArticle(article)}
                                            className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col h-full relative overflow-hidden"
                                        >
                                            {/* Colorful Top Strip */}
                                            <div className={`h-2 w-full bg-gradient-to-r ${gradientColor}`} />

                                            <div className="p-6 flex flex-col h-full">
                                                <div className="flex justify-between items-start mb-4">
                                                    {/* Colorful Badge */}
                                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-xs font-bold uppercase tracking-wide bg-gradient-to-r ${gradientColor}`}>
                                                        <Icon size={12} className="text-white" />
                                                        {category?.name || article.category?.replace("_", " ") || "General"}
                                                    </span>
                                                    {article.videoUrl && (
                                                        <div className="bg-purple-50 p-1.5 rounded-full">
                                                            <Video size={14} className="text-purple-600" />
                                                        </div>
                                                    )}
                                                </div>

                                                <h3 className="font-bold text-lg text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 transition-all leading-tight line-clamp-2">
                                                    {article.title}
                                                </h3>

                                                <p className="text-gray-500 text-sm line-clamp-3 mb-6 flex-1 leading-relaxed">
                                                    {article.content}
                                                </p>

                                                <div className="flex items-center justify-between text-xs text-gray-400 font-medium pt-4 border-t border-gray-50 mt-auto w-full">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${gradientColor} flex items-center justify-center text-white font-bold text-[10px]`}>
                                                            {(article.contributor_name || article.author_name || "A").charAt(0).toUpperCase()}
                                                        </div>
                                                        <span>{article.contributor_name || article.author_name || "Admin"}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <span>{article.views || 0} Views</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
                                <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Search className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900">No guides found</h3>
                                <p className="text-gray-500 text-sm mt-1">Try searching for something else or change category.</p>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Add Article Modal (Admin Only) */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
                        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                            <h2 className="text-lg font-bold text-gray-900">Add New Content</h2>
                            <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleAddArticle} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    required
                                    value={newArticle.title}
                                    onChange={e => setNewArticle({ ...newArticle, title: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="e.g., How to setup Moving Average"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <select
                                    value={newArticle.category}
                                    onChange={e => setNewArticle({ ...newArticle, category: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                >
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Video URL (Optional)</label>
                                <div className="relative">
                                    <Video className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                    <input
                                        type="url"
                                        value={newArticle.videoUrl}
                                        onChange={e => setNewArticle({ ...newArticle, videoUrl: e.target.value })}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                        placeholder="Paste YouTube link here..."
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                                <textarea
                                    required
                                    value={newArticle.content}
                                    onChange={e => setNewArticle({ ...newArticle, content: e.target.value })}
                                    rows={8}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="Write your guide content here..."
                                />
                            </div>

                            <div className="pt-4 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium shadow-md transition-colors disabled:opacity-50 flex items-center gap-2"
                                >
                                    {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
                                    Create Post
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
